import { ApolloError, useMutation } from "@apollo/client";
import * as Sentry from "@sentry/nextjs";
import { MutationCartLinesRemoveArgs } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { toast } from "react-toastify";

import { CART_LINES_REMOVE } from "../graphql/cart";
import { Cart, CartLinesUpdatePayload } from "../types/shopify";

export type UseCartLinesRemoveData = {
  cartLinesRemove?: CartLinesUpdatePayload;
};
export type UseCartLinesRemoveVariables = MutationCartLinesRemoveArgs;

type UseCartLinesRemove = {
  data?: CartLinesUpdatePayload | null | undefined;
  error?: ApolloError;
  loading: boolean;
  removeCartLines: (
    args: UseCartLinesRemoveVariables,
  ) => Promise<Cart | null | undefined>;
};

const useCartLinesRemove = (): UseCartLinesRemove => {
  const [mutation, { data, error: apolloError, loading }] = useMutation<
    UseCartLinesRemoveData,
    UseCartLinesRemoveVariables
  >(CART_LINES_REMOVE);

  if (apolloError) {
    Sentry.captureException(apolloError);
  }

  const removeCartLines = useCallback(
    async ({
      cartId,
      lineIds,
    }: UseCartLinesRemoveVariables): Promise<Cart | null | undefined> => {
      if (cartId) {
        try {
          const { data, errors: mutationErrors } = await mutation({
            variables: { cartId, lineIds },
          });

          if (mutationErrors?.length) {
            mutationErrors.forEach(mutationError => {
              Sentry.captureException(mutationError);
            });

            return null;
          }

          if (data?.cartLinesRemove?.userErrors.length) {
            data.cartLinesRemove.userErrors.forEach(userError => {
              Sentry.captureException(userError);
            });

            return null;
          }

          return data?.cartLinesRemove?.cart;
        } catch (error) {
          toast.error(
            <p data-testid="toast">
              <FormattedMessage id="cart.errorOnUpdatingCart" />
            </p>,
          );

          if (
            error instanceof Error &&
            error.message?.includes("Too many requests")
          ) {
            Sentry.captureMessage("Checkout update: Too many requests", {
              level: "error",
            });
          } else {
            Sentry.captureException(error);
          }
          return null;
        }
      }
      return null;
    },
    [mutation],
  );

  return {
    data: data?.cartLinesRemove,
    error: apolloError,
    loading: typeof window === "undefined" ? false : loading,
    removeCartLines,
  };
};

export default useCartLinesRemove;
